import { makeStyles } from "tss-react/mui";

import { Box, IconButton, Theme, Typography } from "@mui/material";

type SlotStepperInputProps = {
    value: number;
    onChange: (value: number) => void;
    min: number;
    max: number;
    slotLabel: string;
};

const useSlotStepperStyles = makeStyles()((theme: Theme) => ({
    root: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
        flex: 1,
    },
    stepperWrapper: {
        display: "flex",
        alignItems: "center",
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.emteria.border.primary}`,
        overflow: "hidden",
    },
    iconButton: {
        padding: theme.spacing(0.5, 1.25),
        borderRadius: 0,
        "&:hover": { backgroundColor: theme.palette.emteria.background.paperDeep },
    },
    stepperIcon: {
        fontSize: 14,
    },
    valueDisplay: {
        padding: theme.spacing(0.5, 1.5),
        width: 40,
        textAlign: "center",
        fontSize: theme.typography.body2.fontSize,
        fontWeight: 500,
        borderLeft: `1px solid ${theme.palette.emteria.border.primary}`,
        borderRight: `1px solid ${theme.palette.emteria.border.primary}`,
        backgroundColor: theme.palette.emteria.background.paperDeep,
        outline: "none",
        border: "none",
        color: theme.palette.text.primary,
        fontFamily: "inherit",
        boxSizing: "content-box",
        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": { display: "none" },
        "&[type=number]": { MozAppearance: "textfield" },
    },
    slotLabel: { marginRight: theme.spacing(1) },
}));

export const SlotStepperInput = ({ value, onChange, min, max, slotLabel }: SlotStepperInputProps) => {
    const { classes } = useSlotStepperStyles();
    const safeValue = Math.min(Math.max(Number(value) || min, min), max);

    return (
        <Box className={classes.root}>
            <Typography color="text.primary" className={classes.slotLabel}>
                {slotLabel}
            </Typography>
            <Box className={classes.stepperWrapper}>
                <IconButton size="small" onClick={() => onChange(safeValue - 1)} disabled={safeValue <= min} className={classes.iconButton}>
                    <i className={`fa-solid fa-minus ${classes.stepperIcon}`} />
                </IconButton>
                <input
                    type="number"
                    min={min}
                    max={max}
                    className={classes.valueDisplay}
                    value={safeValue}
                    onChange={(e) => onChange(e.target.valueAsNumber)}
                    onBlur={(e) => onChange(Math.min(Math.max(e.target.valueAsNumber || min, min), max))}
                    onFocus={(e) => e.target.select()}
                />
                <IconButton size="small" onClick={() => onChange(safeValue + 1)} disabled={safeValue >= max} className={classes.iconButton}>
                    <i className={`fa-solid fa-plus ${classes.stepperIcon}`} />
                </IconButton>
            </Box>
        </Box>
    );
};
